import React, { useCallback, useState } from 'react';
import {
  Button,
  Flex,
  IconButton,
  Text,
  TextField,
} from '@vibe/core';
import {
  Add,
  Download,
  NavigationChevronLeft,
  NavigationChevronRight,
  DropdownChevronDown,
} from '@vibe/icons';
import ItemTimeTrackingGuide from './ItemTimeTrackingGuide';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subWeeks,
  subMonths,
  subYears,
} from 'date-fns';
import { useScheduleStore } from '../../stores/scheduleStore';
// ─── Types ─────────────────────────────────────────────────────────────────────

type ViewMode = 'table' | 'stack';
interface DateRange {
  start: string;
  end: string;
}

export type PresetKey =
  | 'this_week'
  | 'last_week'
  | 'last_2_weeks'
  | 'this_month'
  | 'last_month'
  | 'this_year'
  | 'last_year'
  | 'custom';

export interface TimeTrackTopBarProps {
  /** Current view mode (table or stack) */
  viewMode: ViewMode;
  /** Callback when the view mode changes */
  onViewModeChange: (mode: ViewMode) => void;
  /** Current date range filter */
  dateRange: DateRange;
  /** Callback when date range changes */
  onDateRangeChange: (range: DateRange) => void;
  /** Formatted date range label */
  dateRangeLabel: string;
  /** Navigate to previous period */
  onDatePrev: () => void;
  /** Navigate to next period */
  onDateNext: () => void;
  /** Callback to open the "Add Worklog" dialog. Omit to hide the button. */
  onAddWorklog?: () => void;
  /** Callback to trigger XLSX export */
  onExport: () => void;
  /** Optional slot rendered at the right end of the top bar (e.g. submit button) */
  rightSlot?: React.ReactNode;
  /** Active preset key (controlled from parent) */
  activePreset?: PresetKey;
  /** Callback when preset changes */
  onPresetChange?: (key: PresetKey) => void;
}

// ─── View mode options ──────────────────────────────────────────────────────

const VIEW_MODES: { key: ViewMode; label: string }[] = [
  { key: 'table', label: 'Table' },
  { key: 'stack', label: 'Stack' },
];

// ─── Preset date ranges ─────────────────────────────────────────────────────

const PRESETS: { key: PresetKey; label: string }[] = [
  { key: 'this_week', label: 'Current week' },
  { key: 'last_week', label: 'Last week' },
  { key: 'last_2_weeks', label: 'Last 2 weeks' },
  { key: 'this_month', label: 'Current month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_year', label: 'Current year' },
  { key: 'last_year', label: 'Last year' },
  { key: 'custom', label: 'Custom' },
];

export function computePresetRange(key: Exclude<PresetKey, 'custom'>, weekStartsOn: 0 | 1 = 1): DateRange {
  const today = new Date();
  switch (key) {
    case 'this_week':
      return {
        start: format(startOfWeek(today, { weekStartsOn }), 'yyyy-MM-dd'),
        end: format(endOfWeek(today, { weekStartsOn }), 'yyyy-MM-dd'),
      };
    case 'last_week': {
      const lw = subWeeks(today, 1);
      return {
        start: format(startOfWeek(lw, { weekStartsOn }), 'yyyy-MM-dd'),
        end: format(endOfWeek(lw, { weekStartsOn }), 'yyyy-MM-dd'),
      };
    }
    case 'last_2_weeks': {
      const twoWeeksAgo = subWeeks(today, 2);
      return {
        start: format(
          startOfWeek(twoWeeksAgo, { weekStartsOn }),
          'yyyy-MM-dd',
        ),
        end: format(endOfWeek(today, { weekStartsOn }), 'yyyy-MM-dd'),
      };
    }
    case 'this_month':
      return {
        start: format(startOfMonth(today), 'yyyy-MM-dd'),
        end: format(endOfMonth(today), 'yyyy-MM-dd'),
      };
    case 'last_month': {
      const lastMonth = subMonths(today, 1);
      return {
        start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
        end: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
      };
    }
    case 'this_year':
      return {
        start: format(startOfYear(today), 'yyyy-MM-dd'),
        end: format(endOfYear(today), 'yyyy-MM-dd'),
      };
    case 'last_year': {
      const lastYear = subYears(today, 1);
      return {
        start: format(startOfYear(lastYear), 'yyyy-MM-dd'),
        end: format(endOfYear(lastYear), 'yyyy-MM-dd'),
      };
    }
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

const TimeTrackTopBar: React.FC<TimeTrackTopBarProps> = ({
  viewMode,
  onViewModeChange,
  dateRange,
  onDateRangeChange,
  dateRangeLabel,
  onDatePrev,
  onDateNext,
  onAddWorklog,
  onExport,
  rightSlot,
  activePreset: controlledPreset,
  onPresetChange,
}) => {
  const firstDayOfWeek = useScheduleStore((s) => s.firstDayOfWeek);
  const [internalPreset, setInternalPreset] = useState<PresetKey>('this_week');
  const [customStart, setCustomStart] = useState(dateRange.start);
  const [customEnd, setCustomEnd] = useState(dateRange.end);
  const [scopeDropdownOpen, setScopeDropdownOpen] = useState(false);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);

  const activePreset = controlledPreset ?? internalPreset;

  const handlePresetClick = useCallback(
    (key: PresetKey) => {
      if (onPresetChange) {
        onPresetChange(key);
      } else {
        setInternalPreset(key);
      }
      if (key !== 'custom') {
        const range = computePresetRange(key, firstDayOfWeek);
        onDateRangeChange(range);
        setScopeDropdownOpen(false);
      } else {
        setScopeDropdownOpen(false);
        setCustomDialogOpen(true);
      }
    },
    [onDateRangeChange, firstDayOfWeek, onPresetChange],
  );

  const handleApplyCustomRange = useCallback(() => {
    if (customStart && customEnd && customStart <= customEnd) {
      onDateRangeChange({ start: customStart, end: customEnd });
      setCustomDialogOpen(false);
    }
  }, [customStart, customEnd, onDateRangeChange]);

  const activePresetLabel = PRESETS.find((p) => p.key === activePreset)?.label ?? 'Current week';

  return (
    <div className="rp-tt-topbar">
      {/* Left section: + Worklog button + view mode + scope */}
      <Flex
        align={Flex.align.CENTER}
        gap={Flex.gaps.SMALL}
        className="rp-tt-topbar__left"
      >
        {/* + Worklog button */}
        {onAddWorklog && (
          <Button
            size={Button.sizes.SMALL}
            leftIcon={Add}
            onClick={onAddWorklog}
          >
            Worklog
          </Button>
        )}

        {/* View mode toggle */}
        <div className="rp-tt-topbar__view-modes">
          {VIEW_MODES.map((mode) => (
            <Button
              key={mode.key}
              size={Button.sizes.SMALL}
              kind={
                viewMode === mode.key
                  ? Button.kinds.PRIMARY
                  : Button.kinds.TERTIARY
              }
              onClick={() => onViewModeChange(mode.key)}
            >
              {mode.label}
            </Button>
          ))}
        </div>

        {/* Scope selector dropdown */}
        <div className="rp-tt-topbar__scope-dropdown-wrapper">
            <button
              className="rp-tt-topbar__scope-trigger"
              onClick={() => setScopeDropdownOpen(!scopeDropdownOpen)}
            >
              <span>{activePresetLabel}</span>
              <DropdownChevronDown size="16" />
            </button>

            {scopeDropdownOpen && (
              <>
                <div
                  className="rp-tt-topbar__scope-backdrop"
                  onClick={() => setScopeDropdownOpen(false)}
                />
                <div className="rp-tt-topbar__scope-menu">
                  {PRESETS.map((preset) => (
                    <button
                      key={preset.key}
                      className={`rp-tt-topbar__scope-option ${
                        activePreset === preset.key
                          ? 'rp-tt-topbar__scope-option--active'
                          : ''
                      }`}
                      onClick={() => handlePresetClick(preset.key)}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </>
            )}
        </div>
      </Flex>

      {/* Right section: date nav + actions */}
      <Flex
        align={Flex.align.CENTER}
        gap={Flex.gaps.SMALL}
        className="rp-tt-topbar__right"
      >
        {/* Date navigation arrows */}
        <IconButton
          icon={NavigationChevronLeft}
          size={IconButton.sizes.SMALL}
          onClick={onDatePrev}
          ariaLabel="Previous period"
        />

        {/* Date range label */}
        <button className="rp-tt-topbar__date-label">
          {dateRangeLabel}
        </button>

        <IconButton
          icon={NavigationChevronRight}
          size={IconButton.sizes.SMALL}
          onClick={onDateNext}
          ariaLabel="Next period"
        />

        {/* Export button */}
        <IconButton
          icon={Download}
          size={IconButton.sizes.SMALL}
          onClick={onExport}
          ariaLabel="Export worklogs"
        />

        {/* How to log work from items guide */}
        <ItemTimeTrackingGuide />

        {/* Optional right slot (e.g. submit for approval button) */}
        {rightSlot}
      </Flex>

      {/* Custom date range dialog */}
      {customDialogOpen && (
        <div className="rp-ts-dialog-overlay" onClick={() => setCustomDialogOpen(false)}>
          <div className="rp-ts-dialog" onClick={(e) => e.stopPropagation()}>
            <Text type={Text.types.TEXT2} weight={Text.weights.MEDIUM}>
              Custom Date Range
            </Text>
            <div className="rp-ts-dialog__body">
              <Flex gap={Flex.gaps.SMALL} align={Flex.align.END}>
                <div className="rp-tt-topbar__custom-field">
                  <label className="rp-tt-topbar__custom-label">From</label>
                  <TextField
                    type={TextField.types.DATE}
                    size={TextField.sizes.SMALL}
                    value={customStart}
                    onChange={(value: string) => setCustomStart(value)}
                  />
                </div>
                <div className="rp-tt-topbar__custom-field">
                  <label className="rp-tt-topbar__custom-label">To</label>
                  <TextField
                    type={TextField.types.DATE}
                    size={TextField.sizes.SMALL}
                    value={customEnd}
                    onChange={(value: string) => setCustomEnd(value)}
                  />
                </div>
              </Flex>
            </div>
            <Flex gap={Flex.gaps.SMALL} justify={Flex.justify.END} className="rp-ts-dialog__actions">
              <Button
                kind={Button.kinds.TERTIARY}
                size={Button.sizes.SMALL}
                onClick={() => setCustomDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button size={Button.sizes.SMALL} onClick={handleApplyCustomRange}>
                Apply
              </Button>
            </Flex>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeTrackTopBar;
